import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Icon, IconButton, SidePanel, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { useAppResetApi } from "@/redux/hooks";
import { clsx } from "@/utils";
import { ClientBookTripButton } from "./ClientBookTripButton";
import { ClientLogo } from "./ClientLogo";
import { useClientNavLinks } from "./ClientNavLinks";

interface ClientDrawerMenuProps {
  open: boolean;
  onClose: () => void;
}

export const ClientDrawerMenu = ({ open, onClose }: ClientDrawerMenuProps) => {
  const resetApi = useAppResetApi();
  const { logout } = useKindeAuth();
  const location = useLocation();
  const phpHostUrl = getPhpHostUrl();
  const clientNavLinks = useClientNavLinks();

  // Close drawer on route change
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleLogout = async () => {
    resetApi();
    if (import.meta.env.MODE === "production") {
      window.location.href = `${phpHostUrl}/logout`;
    } else {
      await logout();
    }
  };

  return (
    <SidePanel open={open} onClose={onClose} width={typeof window !== "undefined" ? window.innerWidth : 300} focused>
      {/* Header */}
      <div className="flex h-[72px] items-center justify-between px-5">
        <ClientLogo />
        <IconButton iconName="close" isCustomIcon variant="tertiary-light" iconSize="lg" onClick={onClose} />
      </div>

      {/* Navigation */}
      <nav className="flex h-full flex-col">
        <div className="no-scrollbar flex-1 overflow-auto">
          <div className="flex flex-col">
            {clientNavLinks.map((link, index) => (
              <NavLink
                key={link.name}
                to={link.to}
                end={link.to === "/client"}
                className={({ isActive }) =>
                  clsx(
                    "group relative flex w-full items-center justify-between px-5 py-3 transition-colors",
                    // Top separator on all except first item; hidden when active or when previous sibling is active
                    index > 0 && [
                      "before:absolute before:inset-x-5 before:top-0 before:border-b before:border-neutral-mid-gray",
                      "[.bg-primary-light+&]:before:border-transparent",
                    ],
                    {
                      "bg-primary-light text-primary": isActive,
                      "before:border-transparent": isActive && index > 0,
                      "text-neutral-black hover:bg-neutral-gray": !isActive,
                    }
                  )
                }
              >
                <Typography variant="action" className="font-medium">
                  {link.name}
                </Typography>
                <Icon name="ArrowRight2" size="sm" />
              </NavLink>
            ))}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="relative flex w-full items-center justify-between px-5 py-3 text-neutral-black transition-colors before:absolute before:inset-x-5 before:top-0 before:border-b before:border-neutral-mid-gray hover:bg-neutral-gray [.bg-primary-light+&]:before:border-transparent"
            >
              <Typography variant="action" className="font-medium">
                Logout
              </Typography>
              <Icon name="LogoutCurve" size="sm" className="rotate-180" />
            </button>
          </div>
        </div>

        {/* Footer — fixed at bottom */}
        <div className="mt-auto flex flex-col gap-y-4 border-t border-neutral-mid-gray px-5 pb-5 pt-4">
          <ClientBookTripButton variant="drawer" />
          <Typography variant="small" className="text-center !text-[10px] text-neutral-dark-gray">
            &copy; {new Date().getFullYear()} - Powered by RideMinder
          </Typography>
        </div>
      </nav>
    </SidePanel>
  );
};
